/**
 * bootstrapBlockRegistry.ts — registers compose/form/view loaders for ControlRenderer.
 */
import {
  setComposeLoader,
  setFormLoader,
  setViewLoader,
  notifyComposeChanged,
  notifyFormChanged,
  notifyViewChanged,
} from '@packages/compose/registry';
import type { RegisteredFormSchema, ComposeSchema } from '@packages/compose/types';

const LAZY_COMPOSE_MODULES = import.meta.glob<{ default: ComposeSchema }>(
  './modules/**/*.compose.json',
);

function composeIdToGlobKey(composeId: string): string | null {
  const match = composeId.match(/^wealthlane:\/\/compose\/([^/]+)\/(.+)$/);
  if (!match) return null;
  const [, entity, name] = match;
  const standardKey = `./modules/${entity}/compose/${name}.compose.json`;
  if (standardKey in LAZY_COMPOSE_MODULES) return standardKey;
  const legacyPrefixKey = `./modules/${entity}/compose/${entity}.${name}.compose.json`;
  if (legacyPrefixKey in LAZY_COMPOSE_MODULES) return legacyPrefixKey;
  return standardKey;
}

export async function loadComposeById(composeId: string): Promise<ComposeSchema> {
  const key = composeIdToGlobKey(composeId);
  if (!key) {
    throw new Error(
      `[domain] Cannot resolve compose id "${composeId}". Expected: wealthlane://compose/<entity>/<name>`,
    );
  }
  const loader = LAZY_COMPOSE_MODULES[key];
  if (!loader) {
    const available = Object.keys(LAZY_COMPOSE_MODULES)
      .map((k) => k.replace('./modules/', '').replace('.compose.json', ''))
      .join(', ');
    throw new Error(`[domain] No compose file for "${key}". Available: ${available}`);
  }
  const mod = await loader();
  return mod.default;
}

// ─── Form schemas (modules/{entity}/form/model.json) ─────────────────────

const RAW_FORM_MODEL = import.meta.glob(
  './modules/*/form/model.json',
  { eager: true },
) as Record<string, { default: Record<string, unknown> }>;

const RAW_FORM_ORCH = import.meta.glob(
  './modules/*/form/orch.json',
  { eager: true },
) as Record<string, { default: Record<string, unknown> }>;

const RAW_FORM_RULES = import.meta.glob(
  './modules/*/form/rules.json',
  { eager: true },
) as Record<string, { default: Record<string, unknown> }>;

/** Side-effect: modules call ResolverRegistry.registerValidation on load. */
import.meta.glob('./modules/*/form/validators.ts', { eager: true });

const RAW_FORM_ALT_MODEL = import.meta.glob(
  './modules/*/form/*-model.json',
  { eager: true },
) as Record<string, { default: Record<string, unknown> }>;

const RAW_FORM_ALT_ORCH = import.meta.glob(
  './modules/*/form/*-orch.json',
  { eager: true },
) as Record<string, { default: Record<string, unknown> }>;

const RAW_FORM_ALT_RULES = import.meta.glob(
  './modules/*/form/*-rules.json',
  { eager: true },
) as Record<string, { default: Record<string, unknown> }>;

const formSchemaMap = new Map<string, RegisteredFormSchema>();
const formDevPathById = new Map<string, string>();

function registerFormSchema(
  modelJson: Record<string, unknown>,
  orchJson: Record<string, unknown>,
  rulesJson: Record<string, unknown>,
  devBase: string,
  catalogKey: string,
): void {
  const schema: RegisteredFormSchema = {
    domain: modelJson,
    orch: orchJson,
    rules: rulesJson,
  };

  formSchemaMap.set(`catalog:${catalogKey}`, schema);
  formSchemaMap.set(`wealthlane://forms/${catalogKey}`, schema);
  formDevPathById.set(`catalog:${catalogKey}`, devBase);
  formDevPathById.set(`wealthlane://forms/${catalogKey}`, devBase);

  const modelId = modelJson.$id as string | undefined;
  if (modelId) {
    formSchemaMap.set(modelId, schema);
    formDevPathById.set(modelId, devBase);
  }
}

for (const [modelPath, modelMod] of Object.entries(RAW_FORM_MODEL)) {
  const modelJson = modelMod.default;
  if (!modelJson) continue;

  const match = modelPath.match(/\/modules\/([^/]+)\/form\/model\.json$/);
  if (!match) continue;
  const [, entityName] = match;

  const base = modelPath.replace(/\/model\.json$/, '');
  const orchMod = RAW_FORM_ORCH[`${base}/orch.json`];
  const rulesMod = RAW_FORM_RULES[`${base}/rules.json`];
  if (!orchMod) continue;

  const devBase = modelPath.replace(/^\.\//, '').replace(/\/model\.json$/, '');
  registerFormSchema(
    modelJson,
    orchMod.default,
    (rulesMod?.default ?? {}) as Record<string, unknown>,
    devBase,
    entityName,
  );
}

for (const [modelPath, modelMod] of Object.entries(RAW_FORM_ALT_MODEL)) {
  const modelJson = modelMod.default;
  if (!modelJson) continue;

  const base = modelPath.replace(/-model\.json$/, '');
  const orchMod = RAW_FORM_ALT_ORCH[`${base}-orch.json`];
  const rulesMod = RAW_FORM_ALT_RULES[`${base}-rules.json`];
  if (!orchMod) continue;

  const slug = base.split('/').pop() ?? 'alt';
  const moduleMatch = modelPath.match(/\/modules\/([^/]+)\/form\//);
  const moduleName = moduleMatch?.[1] ?? 'unknown';
  const catalogKey = slug === 'rename' ? `${moduleName}-rename` : `${moduleName}-${slug}`;

  const devBase = base.replace(/^\.\//, '');
  registerFormSchema(
    modelJson,
    orchMod.default,
    (rulesMod?.default ?? {}) as Record<string, unknown>,
    devBase,
    catalogKey,
  );
}

const RAW_ALT_FORM_MODEL = import.meta.glob(
  './modules/*/form/*-model.json',
  { eager: true },
) as Record<string, { default: Record<string, unknown> }>;

const RAW_ALT_FORM_ORCH = import.meta.glob(
  './modules/*/form/*-orch.json',
  { eager: true },
) as Record<string, { default: Record<string, unknown> }>;

const RAW_ALT_FORM_RULES = import.meta.glob(
  './modules/*/form/*-rules.json',
  { eager: true },
) as Record<string, { default: Record<string, unknown> }>;

for (const [modelPath, modelMod] of Object.entries(RAW_ALT_FORM_MODEL)) {
  const modelJson = modelMod.default;
  if (!modelJson) continue;

  const slugMatch = modelPath.match(/\/modules\/([^/]+)\/form\/([^/]+)-model\.json$/);
  if (!slugMatch) continue;
  const [, entityName, formSlug] = slugMatch;

  const base = modelPath.replace(/-model\.json$/, '');
  const orchMod = RAW_ALT_FORM_ORCH[`${base}-orch.json`];
  const rulesMod = RAW_ALT_FORM_RULES[`${base}-rules.json`];
  if (!orchMod) continue;

  const altSchema: RegisteredFormSchema = {
    domain: modelJson,
    orch: orchMod.default,
    rules: rulesMod?.default ?? {},
  };

  const devBase = modelPath.replace(/^\.\//, '').replace(/-model\.json$/, '');
  const modelId = (modelJson as Record<string, unknown>).$id as string | undefined;
  if (modelId) {
    formSchemaMap.set(modelId, altSchema);
    formDevPathById.set(modelId, devBase);
  }
  formSchemaMap.set(`catalog:${entityName}/${formSlug}`, altSchema);
}

// ─── Explicit registration (supplements glob — ensures newly-added files are
//     always available without requiring a dev-server restart) ────────────────

const EXPLICIT_FORM_SCHEMAS: Array<{
  entityName: string;
  model: Record<string, unknown>;
  orch: Record<string, unknown>;
  rules: Record<string, unknown>;
}> = [];

for (const { entityName, model, orch, rules } of EXPLICIT_FORM_SCHEMAS) {
  // Only register if the glob didn't already pick this entity up
  if (!formSchemaMap.has(`wealthlane://forms/${entityName}`)) {
    registerFormSchema(
      model,
      orch,
      rules,
      `modules/${entityName}/form`,
      entityName,
    );
  }
}

const formLoaderFn = async (blockId: string) => {
  const key = blockId.replace(/#block$/, '');
  const entry = formSchemaMap.get(blockId) ?? formSchemaMap.get(key) ?? null;
  if (!entry) return null;

  if (import.meta.env.DEV) {
    const devBase = formDevPathById.get(blockId) ?? formDevPathById.get(key);
    if (devBase) {
      const base = `/src/packages/domain/src/${devBase}`;
      const ts = Date.now();
      const isAltForm = devBase.includes('/form/') && !devBase.endsWith('/form');
      const altSlug = isAltForm ? devBase.split('/').pop() : null;
      const altParent = isAltForm ? devBase.replace(/\/[^/]+$/, '') : null;
      try {
        const [modelRes, orchRes, rulesRes] = await Promise.all([
          fetch(
            isAltForm && altSlug && altParent
              ? `/src/packages/domain/src/${altParent}/${altSlug}-model.json?t=${ts}`
              : `${base}/model.json?t=${ts}`,
          ),
          fetch(
            isAltForm && altSlug && altParent
              ? `/src/packages/domain/src/${altParent}/${altSlug}-orch.json?t=${ts}`
              : `${base}/orch.json?t=${ts}`,
          ),
          fetch(
            isAltForm && altSlug && altParent
              ? `/src/packages/domain/src/${altParent}/${altSlug}-rules.json?t=${ts}`
              : `${base}/rules.json?t=${ts}`,
          ).catch(() => null),
        ]);
        if (modelRes.ok && orchRes.ok) {
          const domain = await modelRes.json();
          const orch = await orchRes.json();
          const rules = rulesRes?.ok ? await rulesRes.json() : {};
          return { domain, orch, rules };
        }
      } catch {
        // cached entry fallback
      }
    }
  }

  return entry;
};

// ─── View schemas (modules/{entity}/views/{view}/{view}.model.json) ─────────

const LAZY_VIEW_MODEL = import.meta.glob<{ default: Record<string, unknown> }>(
  './modules/**/views/**/*.model.json',
);

const LAZY_VIEW_ORCH = import.meta.glob<{ default: Record<string, unknown> }>(
  './modules/**/views/**/*.orch.json',
);

function viewBlockIdToGlobKeys(blockId: string): { modelKey: string; orchKey: string } | null {
  const normalized = blockId.replace(/#block$/, '');
  const match = normalized.match(/^wealthlane:\/\/views\/([^/]+)\/(.+)$/);
  if (!match) return null;
  const [, entity, viewName] = match;
  return {
    modelKey: `./modules/${entity}/views/${viewName}/${viewName}.model.json`,
    orchKey: `./modules/${entity}/views/${viewName}/${viewName}.orch.json`,
  };
}

async function loadViewSchemaFiles(
  keys: { modelKey: string; orchKey: string },
): Promise<{ domain: Record<string, unknown>; orch: Record<string, unknown> } | null> {
  if (import.meta.env.DEV) {
    const toDevUrl = (key: string) =>
      `/src/packages/domain/src/${key.replace(/^\.\//, '')}?t=${Date.now()}`;
    const [modelRes, orchRes] = await Promise.all([
      fetch(toDevUrl(keys.modelKey)),
      fetch(toDevUrl(keys.orchKey)),
    ]);
    if (modelRes.ok && orchRes.ok) {
      return {
        domain: (await modelRes.json()) as Record<string, unknown>,
        orch: (await orchRes.json()) as Record<string, unknown>,
      };
    }
  }

  const loadModel = LAZY_VIEW_MODEL[keys.modelKey];
  const loadOrch = LAZY_VIEW_ORCH[keys.orchKey];
  if (!loadModel || !loadOrch) return null;

  const [modelMod, orchMod] = await Promise.all([loadModel(), loadOrch()]);
  return { domain: modelMod.default, orch: orchMod.default };
}

const viewLoaderFn = async (blockId: string) => {
  const keys = viewBlockIdToGlobKeys(blockId);
  if (!keys) return null;
  return loadViewSchemaFiles(keys);
};

export function initDomainLoaders(): void {
  setComposeLoader(loadComposeById);
  setFormLoader(formLoaderFn);
  setViewLoader(viewLoaderFn);
}

if (import.meta.hot) {
  import.meta.hot.accept(Object.keys(LAZY_COMPOSE_MODULES), () => {
    notifyComposeChanged();
  });

  import.meta.hot.on('schema:update', (data: { path: string; category: string }) => {
    switch (data.category) {
      case 'form':
      case 'module':
        notifyFormChanged();
        break;
      case 'view':
        notifyViewChanged();
        break;
      default:
        notifyFormChanged();
        notifyViewChanged();
        break;
    }
  });
}
